import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Form, Input, Button, Select, message, Spin, Tag, DatePicker, 
  InputNumber, Row, Col, Slider
} from "antd";
import { 
  ArrowLeftOutlined, ExportOutlined, ProjectOutlined,
  CheckCircleOutlined, FileTextOutlined, PlusOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const TECHNOLOGY_OPTIONS = [
  "React", "Angular", "Vue.js", "Node.js", "Python", "Java", "Go", "Rust",
  "TypeScript", "JavaScript", "PostgreSQL", "MongoDB", "Redis", "AWS", 
  "Azure", "GCP", "Docker", "Kubernetes", "GraphQL", "REST API",
  "Next.js", "Express.js", "Django", "FastAPI", "Spring Boot", ".NET",
  "Flutter", "React Native", "Swift", "Kotlin", "TensorFlow", "PyTorch"
];

const ROLE_OPTIONS = [
  "Frontend Developer", "Backend Developer", "Full Stack Developer",
  "DevOps Engineer", "Data Engineer", "ML Engineer", "QA Engineer",
  "Tech Lead", "Solution Architect", "Project Manager", "UI/UX Designer"
];

const complexityMarks = {
  0: "Simple",
  25: "Low",
  50: "Medium",
  75: "High",
  100: "Very Complex"
};

const maturityMarks = {
  1: "1",
  2: "2",
  3: "3",
  4: "4",
  5: "5"
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding: 40px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
`;

const FormCard = styled.div`
  max-width: 1000px;
  width: 100%;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #014c75 0%, #0277a8 100%);
  padding: 24px 32px;
  color: white;

  .header-content {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .back-btn {
    background: rgba(255, 255, 255, 0.15);
    border: none;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
    }
  }

  .header-icon {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .header-text {
    flex: 1;

    h1 {
      font-size: 22px;
      font-weight: 700;
      margin: 0 0 4px 0;
    }

    p {
      font-size: 14px;
      opacity: 0.85;
      margin: 0;
    }
  }
`;

const Content = styled.div`
  padding: 32px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;

  .section {
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e8e8e8;

    &:last-child {
      margin-bottom: 0;
      border-bottom: none;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    .step-number {
      width: 24px;
      height: 24px;
      background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 12px;
      font-weight: 700;
    }
  }

  .form-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    margin: 24px 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .ant-form-item {
    margin-bottom: 16px;

    .ant-form-item-label > label {
      font-size: 14px;
      font-weight: 600;
      color: #334155;
    }

    .ant-input,
    .ant-select-selector,
    .ant-picker,
    .ant-input-number {
      border-radius: 8px !important;
      border: 1px solid #e2e8f0 !important;

      &:hover {
        border-color: #01d9a9 !important;
      }

      &:focus,
      &.ant-select-focused {
        border-color: #01d9a9 !important;
        box-shadow: 0 0 0 2px rgba(1, 217, 169, 0.1) !important;
      }
    }
  }

  .assessment-preview {
    background: linear-gradient(135deg, #f0fdf9 0%, #e8f4f8 100%);
    border: 1px solid #01d9a9;
    border-radius: 12px;
    padding: 20px;
    margin-top: 12px;

    .preview-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .preview-title {
      font-size: 15px;
      font-weight: 600;
      color: #014c75;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .view-link {
      font-size: 13px;
      color: #0284c7;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;

      &:hover {
        color: #0369a1;
        text-decoration: underline;
      }
    }

    .preview-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;

      @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    .preview-item {
      .label {
        font-size: 11px;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
      }

      .value {
        font-size: 14px;
        font-weight: 500;
        color: #1e293b;
      }
    }

    .tech-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 4px;
    }
  }

  .project-details-section {
    background: #fafbfc;
    border-radius: 12px;
    padding: 24px;
    border: 1px solid #e8e8e8;
  }

  .slider-wrapper {
    padding: 0 8px;
    
    .slider-value {
      text-align: center;
      font-size: 16px;
      font-weight: 600;
      color: #014c75;
      margin-bottom: 8px;
    }

    .slider-labels {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: #94a3b8;
      margin-bottom: 4px;
    }
  }

  .option-cards {
    display: flex;
    gap: 16px;

    .option-card {
      flex: 1;
      padding: 16px;
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      cursor: pointer;
      text-align: center;
      transition: all 0.2s;

      &:hover {
        border-color: #01d9a9;
      }

      &.selected {
        border-color: #01d9a9;
        background: rgba(1, 217, 169, 0.05);
      }

      .option-icon {
        font-size: 24px;
        margin-bottom: 8px;
      }

      .option-label {
        font-size: 13px;
        font-weight: 500;
        color: #334155;
      }
    }
  }

  .custom-tech-input {
    display: flex;
    gap: 8px;
    margin-top: 12px;

    .ant-input {
      flex: 1;
    }

    .add-tech-btn {
      background: #01d9a9;
      border: none;
      color: white;
    }
  }
`;

const Footer = styled.div`
  padding: 20px 32px;
  border-top: 1px solid #e8e8e8;
  background: #fafafa;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .cancel-btn {
    height: 44px;
    padding: 0 24px;
    border-radius: 10px;
    font-weight: 500;
    border: 1px solid #e2e8f0;
    background: white;
    color: #64748b;

    &:hover {
      border-color: #014c75;
      color: #014c75;
    }
  }

  .submit-btn {
    height: 44px;
    padding: 0 32px;
    border-radius: 10px;
    font-weight: 600;
    background: linear-gradient(135deg, #01d9a9 0%, #00b894 100%);
    border: none;
    color: white;
    box-shadow: 0 4px 12px rgba(1, 217, 169, 0.3);

    &:hover {
      background: linear-gradient(135deg, #00e8b5 0%, #01d9a9 100%);
    }

    &:disabled {
      background: #e2e8f0;
      color: #94a3b8;
      box-shadow: none;
    }
  }
`;

const CreateProjectFromAssessment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedAssessmentId = searchParams.get('assessmentId');
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [engagements, setEngagements] = useState([]);
  const [loadingEngagements, setLoadingEngagements] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [customTech, setCustomTech] = useState("");
  const [complexityLevel, setComplexityLevel] = useState(50);
  const [maturityIndex, setMaturityIndex] = useState(3);
  const [dataSensitivity, setDataSensitivity] = useState(false);

  useEffect(() => {
    fetchAssessments();
    fetchEngagements();
  }, []);

  useEffect(() => {
    if (preselectedAssessmentId && assessments.length > 0) {
      const found = assessments.find(a => a.id === preselectedAssessmentId);
      if (found) {
        form.setFieldValue('assessmentId', preselectedAssessmentId);
        handleAssessmentChange(preselectedAssessmentId);
      }
    }
  }, [preselectedAssessmentId, assessments]);

  const fetchAssessments = async () => {
    setLoadingAssessments(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_AI_PROJECT_ASSESSMENTS,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data.assessments || [];
      setAssessments(data);
    } catch (error) {
      console.error("Error fetching assessments:", error);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const fetchEngagements = async () => {
    setLoadingEngagements(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_ACTIVE_ENGAGEMENTS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data.engagements || [];
      setEngagements(data);
    } catch (error) {
      console.error("Error fetching engagements:", error);
    } finally {
      setLoadingEngagements(false);
    }
  };

  const handleAssessmentChange = async (assessmentId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_AI_PROJECT_ASSESSMENT}/${assessmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const assessment = response.data.assessment;
      setSelectedAssessment(assessment);

      const technologies = assessment.technologies || [];
      const report = assessment.report || assessment.assessmentReport || {};
      const surveyResponses = assessment.surveyResponses || {};
      
      const teamSize = report.resourceStrategy?.impactTeam?.reduce((sum, m) => sum + (m.quantity || 0), 0) || 
                       report.resourceStrategy?.recommendedTeamSize || 0;
      const budget = report.resourceStrategy?.totalImpactTeamCost || 
                     report.budgetEstimation?.totalBudget || 0;
      
      const criticalRoles = report.resourceStrategy?.impactTeam?.map(m => m.role) || 
                           report.resourceStrategy?.criticalRoles || [];

      let deliveryType = surveyResponses.deliveryType || '';
      if (deliveryType === 'new_development') deliveryType = 'build';
      else if (deliveryType === 'enhancement') deliveryType = 'enhance';
      else if (deliveryType === 'migration') deliveryType = 'migrate';

      const architectureType = surveyResponses.architecturePreference || surveyResponses.architectureType || '';
      const integrationComplexity = surveyResponses.integrationComplexity || '';
      
      let complexityVal = 50;
      if (surveyResponses.projectComplexity === 'simple') complexityVal = 0;
      else if (surveyResponses.projectComplexity === 'low') complexityVal = 25;
      else if (surveyResponses.projectComplexity === 'medium') complexityVal = 50;
      else if (surveyResponses.projectComplexity === 'high') complexityVal = 75;
      else if (surveyResponses.projectComplexity === 'very_complex') complexityVal = 100;

      let maturityVal = surveyResponses.maturityLevel || surveyResponses.maturityIndex || 3;
      if (typeof maturityVal === 'string') {
        maturityVal = parseInt(maturityVal) || 3;
      }

      const dataSens = surveyResponses.dataSensitivity === 'yes' || 
                       surveyResponses.dataSensitivity === true ||
                       surveyResponses.securityRequirements === 'high';

      setComplexityLevel(complexityVal);
      setMaturityIndex(maturityVal);
      setDataSensitivity(dataSens);

      form.setFieldsValue({
        title: `Project - ${assessment.requirementDescription?.substring(0, 50) || 'New Project'}`,
        description: assessment.requirementDescription || '',
        primaryTechnologies: technologies.slice(0, 3),
        secondaryTechnologies: technologies.slice(3),
        expectedTeamSize: teamSize,
        budgetAllocated: budget,
        deliveryType: deliveryType,
        architectureType: architectureType,
        integrationComplexity: integrationComplexity,
        complexityLevel: complexityVal,
        maturityIndex: maturityVal,
        dataSensitivity: dataSens,
        criticalRoles: criticalRoles,
      });
    } catch (error) {
      console.error("Error fetching assessment details:", error);
      message.error("Failed to load assessment details");
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssessment = () => {
    if (selectedAssessment) {
      window.open(`/customer/assessments/${selectedAssessment.id}`, '_blank');
    }
  };

  const addCustomTechnology = () => {
    if (customTech.trim()) {
      const current = form.getFieldValue("primaryTechnologies") || [];
      if (!current.includes(customTech.trim())) {
        form.setFieldsValue({
          primaryTechnologies: [...current, customTech.trim()]
        });
      }
      setCustomTech("");
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      
      if (!selectedAssessment) {
        message.warning("Please select an assessment");
        return;
      }

      if (!values.engagementId) {
        message.warning("Please select an engagement");
        return;
      }

      setSubmitting(true);
      const token = localStorage.getItem("authToken");
      
      const projectData = {
        engagementId: values.engagementId,
        title: values.title,
        description: values.description,
        projectObjective: values.description,
        creationMode: "AI_ASSESSMENT",
        primaryTechnologies: values.primaryTechnologies || [],
        secondaryTechnologies: values.secondaryTechnologies || [],
        expectedTeamSize: values.expectedTeamSize,
        budgetAllocated: values.budgetAllocated,
        expectedStartDate: values.expectedStartDate?.format("YYYY-MM-DD"),
        expectedEndDate: values.expectedEndDate?.format("YYYY-MM-DD"),
        deliveryType: values.deliveryType,
        architectureType: values.architectureType,
        integrationComplexity: values.integrationComplexity,
        complexityLevel: complexityLevel,
        maturityIndex: maturityIndex,
        dataSensitivity: dataSensitivity,
        criticalRoles: values.criticalRoles || [],
      };

      const response = await axios.post(
        API_CONST.CREATE_PROJECT,
        projectData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        message.success("Project created successfully!");
        navigate(`/customer/projects/details/${response.data.project?.id}`);
      } else {
        message.error(response.data.error || "Failed to create project");
      }
    } catch (error) {
      console.error("Error creating project:", error);
      message.error(error.response?.data?.error || "Failed to create project");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <FormCard>
        <Header>
          <div className="header-content">
            <button className="back-btn" onClick={() => navigate("/customer/projects/create")}>
              <ArrowLeftOutlined />
            </button>
            <div className="header-icon">
              <ProjectOutlined />
            </div>
            <div className="header-text">
              <h1>Create Project from Assessment</h1>
              <p>Use an existing AI assessment to pre-fill project details</p>
            </div>
          </div>
        </Header>

        <Content>
          <Form form={form} layout="vertical">
            <div className="section">
              <div className="section-title">
                <span className="step-number">1</span>
                Select Assessment
              </div>
              
              <Form.Item
                name="assessmentId"
                label="Assessment"
                rules={[{ required: true, message: "Please select an assessment" }]}
              >
                <Select
                  placeholder="Choose a completed assessment"
                  loading={loadingAssessments}
                  onChange={handleAssessmentChange}
                  size="large"
                  showSearch
                  optionFilterProp="children"
                >
                  {assessments.map(a => (
                    <Option key={a.id} value={a.id}>
                      {a.id} - {a.requirementDescription?.substring(0, 50) || 'Assessment'}...
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {loading && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <Spin tip="Loading assessment details..." />
                </div>
              )}

              {selectedAssessment && !loading && (
                <div className="assessment-preview">
                  <div className="preview-header">
                    <div className="preview-title">
                      <CheckCircleOutlined style={{ color: '#01d9a9' }} />
                      Assessment Loaded - Fields Auto-Filled
                    </div>
                    <div className="view-link" onClick={handleViewAssessment}>
                      <FileTextOutlined />
                      View Full Assessment
                      <ExportOutlined style={{ fontSize: 10 }} />
                    </div>
                  </div>
                  <div className="preview-grid">
                    <div className="preview-item">
                      <div className="label">Assessment ID</div>
                      <div className="value">{selectedAssessment.id}</div>
                    </div>
                    <div className="preview-item">
                      <div className="label">Created</div>
                      <div className="value">
                        {selectedAssessment.createdAt ? dayjs(selectedAssessment.createdAt).format('MMM DD, YYYY') : '-'}
                      </div>
                    </div>
                    <div className="preview-item">
                      <div className="label">Status</div>
                      <div className="value" style={{ color: '#01d9a9' }}>
                        {selectedAssessment.status}
                      </div>
                    </div>
                  </div>
                  <div className="preview-item" style={{ marginTop: 12 }}>
                    <div className="label">Technologies</div>
                    <div className="tech-tags">
                      {(selectedAssessment.technologies || []).slice(0, 8).map((tech, i) => (
                        <Tag key={i} color={i < 3 ? "blue" : "default"}>{tech}</Tag>
                      ))}
                      {(selectedAssessment.technologies || []).length > 8 && (
                        <Tag>+{selectedAssessment.technologies.length - 8} more</Tag>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="section">
              <div className="section-title">
                <span className="step-number">2</span>
                Select Engagement
              </div>
              <Form.Item
                name="engagementId"
                label="Engagement"
                rules={[{ required: true, message: "Please select an engagement" }]}
              >
                <Select
                  placeholder="Choose an active engagement"
                  loading={loadingEngagements}
                  size="large"
                  showSearch
                  optionFilterProp="children"
                >
                  {engagements.map(e => (
                    <Option key={e.id} value={e.id}>
                      {e.id} - {e.title}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {selectedAssessment && (
              <>
                <div className="section">
                  <div className="section-title">
                    <span className="step-number">3</span>
                    Project Information
                  </div>
                  <div className="project-details-section">
                    <Form.Item
                      name="title"
                      label="Project Name"
                      rules={[{ required: true, message: "Please enter project name" }]}
                    >
                      <Input placeholder="e.g., E-Commerce Platform Rebuild" size="large" />
                    </Form.Item>

                    <Form.Item
                      name="description"
                      label="Project Description / Requirement"
                    >
                      <TextArea 
                        rows={4} 
                        placeholder="Describe the project requirements, scope, and goals..."
                        size="large"
                      />
                    </Form.Item>

                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          name="expectedStartDate"
                          label="Expected Start Date"
                        >
                          <DatePicker 
                            style={{ width: "100%" }}
                            size="large"
                            placeholder="Select start date"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="expectedEndDate"
                          label="Expected End Date"
                        >
                          <DatePicker 
                            style={{ width: "100%" }}
                            size="large"
                            placeholder="Select end date"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          name="deliveryType"
                          label="Delivery Type"
                        >
                          <Select placeholder="What type of work is this?" size="large">
                            <Option value="build">Build - New Development</Option>
                            <Option value="enhance">Enhance - Improve Existing</Option>
                            <Option value="migrate">Migrate - Platform Migration</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="budgetAllocated"
                          label="Project Budget (INR)"
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Enter budget in INR"
                            formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
                            size="large"
                            min={0}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="section">
                  <div className="section-title">
                    <span className="step-number">4</span>
                    Technology Stack
                  </div>
                  <div className="project-details-section">
                    <Form.Item
                      name="primaryTechnologies"
                      label="Primary Technologies (Top 3)"
                      rules={[{ required: true, message: "Please select at least one technology" }]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select primary technologies"
                        size="large"
                        allowClear
                        showSearch
                      >
                        {TECHNOLOGY_OPTIONS.map((tech) => (
                          <Option key={tech} value={tech}>{tech}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <div className="custom-tech-input">
                      <Input
                        placeholder="Add a custom technology"
                        value={customTech}
                        onChange={(e) => setCustomTech(e.target.value)}
                        onPressEnter={addCustomTechnology}
                        size="large"
                      />
                      <Button 
                        onClick={addCustomTechnology} 
                        icon={<PlusOutlined />}
                        className="add-tech-btn"
                        size="large"
                      >
                        Add
                      </Button>
                    </div>

                    <Form.Item
                      name="secondaryTechnologies"
                      label="Secondary Technologies"
                      style={{ marginTop: 24 }}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Select supporting technologies"
                        size="large"
                        allowClear
                        showSearch
                      >
                        {TECHNOLOGY_OPTIONS.map((tech) => (
                          <Option key={tech} value={tech}>{tech}</Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <div className="form-section-title">Architecture</div>

                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          name="architectureType"
                          label="Architecture Type"
                        >
                          <Select placeholder="Choose architecture style" size="large">
                            <Option value="monolith">Monolith</Option>
                            <Option value="microservices">Microservices</Option>
                            <Option value="hybrid">Hybrid</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="integrationComplexity"
                          label="Integration Complexity"
                        >
                          <Select placeholder="How complex are integrations?" size="large">
                            <Option value="low">Low</Option>
                            <Option value="medium">Medium</Option>
                            <Option value="high">High</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </div>

                <div className="section">
                  <div className="section-title">
                    <span className="step-number">5</span>
                    Complexity & Team
                  </div>
                  <div className="project-details-section">
                    <Form.Item
                      name="complexityLevel"
                      label="Overall Complexity"
                    >
                      <div className="slider-wrapper">
                        <div className="slider-value">
                          {complexityMarks[complexityLevel] || "Medium"}
                        </div>
                        <div className="slider-labels">
                          <span>Simple</span>
                          <span>Complex</span>
                        </div>
                        <Slider
                          value={complexityLevel}
                          marks={complexityMarks}
                          step={25}
                          onChange={(value) => {
                            setComplexityLevel(value);
                            form.setFieldsValue({ complexityLevel: value });
                          }}
                        />
                      </div>
                    </Form.Item>

                    <Form.Item
                      name="maturityIndex"
                      label="Maturity Index"
                      style={{ marginTop: 32 }}
                    >
                      <div className="slider-wrapper">
                        <div className="slider-value">
                          Level {maturityIndex}
                        </div>
                        <div className="slider-labels">
                          <span>Initial</span>
                          <span>Optimizing</span>
                        </div>
                        <Slider
                          value={maturityIndex}
                          min={1}
                          max={5}
                          marks={maturityMarks}
                          step={1}
                          onChange={(value) => {
                            setMaturityIndex(value);
                            form.setFieldsValue({ maturityIndex: value });
                          }}
                        />
                      </div>
                    </Form.Item>

                    <div className="form-section-title" style={{ marginTop: 32 }}>Security & Team</div>

                    <Form.Item
                      name="dataSensitivity"
                      label="Data Sensitivity"
                    >
                      <div className="option-cards">
                        <div 
                          className={`option-card ${!dataSensitivity ? "selected" : ""}`}
                          onClick={() => {
                            setDataSensitivity(false);
                            form.setFieldsValue({ dataSensitivity: false });
                          }}
                        >
                          <div className="option-icon">🔓</div>
                          <div className="option-label">No Special Requirements</div>
                        </div>
                        <div 
                          className={`option-card ${dataSensitivity ? "selected" : ""}`}
                          onClick={() => {
                            setDataSensitivity(true);
                            form.setFieldsValue({ dataSensitivity: true });
                          }}
                        >
                          <div className="option-icon">🔐</div>
                          <div className="option-label">Security Sensitive</div>
                        </div>
                      </div>
                    </Form.Item>

                    <Row gutter={24} style={{ marginTop: 24 }}>
                      <Col span={12}>
                        <Form.Item
                          name="expectedTeamSize"
                          label="Expected Team Size"
                        >
                          <InputNumber
                            min={1}
                            max={100}
                            placeholder="Team size"
                            style={{ width: "100%" }}
                            size="large"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="criticalRoles"
                          label="Critical Roles"
                        >
                          <Select
                            mode="multiple"
                            placeholder="Select key roles"
                            size="large"
                            allowClear
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <Option key={role} value={role}>{role}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </div>
              </>
            )}
          </Form>
        </Content>

        <Footer>
          <Button className="cancel-btn" onClick={() => navigate("/customer/projects/create")}>
            Cancel
          </Button>
          <Button 
            className="submit-btn" 
            onClick={handleSubmit}
            loading={submitting}
            disabled={!selectedAssessment}
          >
            Create Project
          </Button>
        </Footer>
      </FormCard>
    </PageWrapper>
  );
};

export default CreateProjectFromAssessment;
